import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { GeneralLedgerPage } from "@components/accounting/generalLedger/GeneralLedgerPage";
import { GeneralLedgerJournalEntryRoute } from "@components/accounting/generalLedger/GeneralLedgerJournalEntryRoute";
import { StateAwareNavigate } from "@components/routes/StateAwareNavigate";
import React from "react";
import { Outlet, Route, Routes } from "react-router-dom";
import { AccrualImporterDraftsPage } from "@components/accrualDocumentImporter/AccrualImporterDraftsPage";
import { BillsPage } from "@components/bills/BillsPage";
import { EntityProvider } from "@components/entity/EntityProvider";
import { UpdateEntityProfileModal } from "@components/settings/EntitySettings/Modals/UpdateEntityProfileModal";
import { FeatureAccess } from "@components/settings/FeatureAccess/FeatureAccess";
import { CreateOrganizationRoute } from "@components/organization/CreateOrganizationRoute";
import { TasksPage } from "@components/tasks/TasksPage";
import { TasksMainInterface } from "@components/tasks/TasksMainInterface";
import { DirectoryToCounterpartiesRedirect } from "@components/settings/Directory/DirectoryToCounterpartiesRedirect";
import { DirectorySettings } from "@components/settings/Directory/DirectorySettings";
import { getActionsTasksAccountsNavPaths } from "@components/navigation/getActionsTasksAccountsNavPaths";
import { HistoryPage } from "@components/history/HistoryPage";
import { useCanUseConsolidation } from "@hooks/useCanUseConsolidation";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { TrialBalancePage } from "@components/accounting/trialBalance/TrialBalancePage";
import { SchedulesReportPageWrapper } from "@components/accounting/schedulesReport/SchedulesReportPageWrapper";
import { BalanceSheetPreviewPage } from "@components/accounting/balanceSheet/BalanceSheetPreviewPage";
import { EliminationDetailPreviewPage } from "@components/accounting/eliminationDetail/EliminationDetailPreviewPage";
import { EliminationExceptionsPreviewPage } from "@components/accounting/eliminationExceptions/EliminationExceptionsPreviewPage";
import { IntercompanyTransactionsPreviewPage } from "@components/accounting/intercompanyTransactions/IntercompanyTransactionsPreviewPage";
import { LegacyIntercompanyTransactionsRedirect } from "@components/accounting/intercompanyTransactions/LegacyIntercompanyTransactionsRedirect";
import { ReportingPage } from "@components/reporting/ReportingPage";
import { REPORTING_ROUTE } from "@components/reporting/reportingRoutes";
import { FIXED_ASSETS_PATH } from "@components/navigation/sideNav/workspaceSideNavSections";
import { ChartOfAccountsPage } from "../accounting/chartOfAccounts/ChartOfAccounts";
import { ProfitLossPreviewPage } from "../accounting/profitLoss/ProfitLossPreviewPage";
import { CashFlowStatementPage } from "../accounting/cashFlowStatement/CashFlowStatementPage";
import { ExpenseByVendorPage } from "../accounting/expenseByVendor/ExpenseByVendorPage";
import { ArAgingSummaryPageWrapper } from "../accounting/arAging/ArAgingSummaryPageWrapper";
import { ArAgingDetailPageWrapper } from "../accounting/arAging/ArAgingDetailPageWrapper";
import { CheckDetailPage } from "../accounting/checkDetail/CheckDetailPage";
import { Form1099NecPage } from "../accounting/form1099Nec/Form1099NecPage";
import { AccountingPage } from "../accounting/AccountingPage";
import { AccountingTabProvider } from "../accounting/AccountingTabProvider";
import { ReconciliationPageWrapper } from "../accounting/reconciliation/ReconciliationPageWrapper";
import { BankReconciliationReportPreviewPage } from "../accounting/reconciliation/report/BankReconciliationReportPreviewPage";
import { AccrualReviewPageContainer } from "../accounting/accrualReview/AccrualReviewPageContainer";
import { FixedAssetsPage } from "../accounting/fixedAssets/FixedAssetsPage";
import { SchedulesPage } from "../accounting/schedules/SchedulesPage";
import { ScheduleHubScopeProvider } from "../accounting/schedules/ScheduleHubScopeProvider";
import {
    ScheduleHubScope,
    getAvailableScheduleHubScopes,
} from "../accounting/schedules/scheduleHubScope";
import { SchedulesToRevenueRedirect } from "../accounting/schedules/SchedulesToRevenueRedirect";

import { AccountsPage } from "../accounts/AccountsPage";
import { AddAccountsRoute } from "../accounts/AddAccounts/AddAccountsRoute";
import { NewEntityLegacyPlusGuard } from "../billing/LegacyPlusEntityLimit/NewEntityLegacyPlusGuard";
import { DocsToDocumentsRedirect } from "../documents/DocsToDocumentsRedirect";
import { FinancialDocumentsPage } from "../documents/FinancialDocumentsPage";
import { FinancialDocumentUploadProvider } from "../documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { InvoicesPage } from "../invoices/InvoicesPage";
import { InvoicePreviewPage } from "../invoices/InvoicePreviewPage";
import { CatalogItemsPage } from "../items/CatalogItemsPage";
import { InvoiceTemplatesPage } from "../invoices/templates/InvoiceTemplatesPage";
import { OwnershipPage } from "../ownership/OwnershipPage";
import { MainInterface } from "../mainInterface/MainInterface";
import { CashFlowPage } from "../reports/CashFlowPage";
import { CategoriesPage } from "../settings/Categories/CategoriesPage";
import { DocumentCategoriesPage } from "../settings/DocumentCategories/DocumentCategoriesPage";
import { ClassesPage } from "../settings/Classes/ClassesPage";
import { RulesPage } from "../settings/Rules/RulesPage";
import { SettingsRoute } from "../settings/SettingsRoute";
import { TransactionsPageContainer } from "../transactions/TransactionsPageContainer";
import { CashflowToInsightsRedirect } from "./CashflowToInsightsRedirect";
import { useAccountingBreadcrumbs } from "./useAccountingBreadcrumbs";
import { useEntityWithMissingBusinessProfile } from "./useEntityWithMissingBusinessProfile";
import { useRenderCommonRoutes } from "./useRenderCommonRoutes";
import { useRenderOrganizationRedirectRoutes } from "./useRenderOrganizationRedirectRoutes";
import { InvoicingRouteAccess } from "@components/invoices/InvoicingAccess";

const FixedAssetsRoute = () => (
    <FeatureAccess
        feature={PREMIUM_FEATURES.FIXED_ASSETS}
        renderAllowed={() => (
            <MainInterface
                breadcrumbs={[
                    { label: "Accounting", to: "/accounting" },
                    { label: "Fixed assets" },
                ]}
            >
                <FixedAssetsPage />
            </MainInterface>
        )}
        renderBlocked={() => (
            <StateAwareNavigate to="/settings/plans" replace />
        )}
    />
);

const ScheduleHubRoute = ({ scope }: { scope: ScheduleHubScope }) => (
    <FeatureAccess
        feature={PREMIUM_FEATURES.SCHEDULES}
        renderAllowed={() => (
            <MainInterface
                breadcrumbs={[
                    { label: "Accounting", to: "/accounting" },
                    { label: scope.label },
                ]}
            >
                <ScheduleHubScopeProvider key={scope.path} scope={scope}>
                    <SchedulesPage />
                </ScheduleHubScopeProvider>
            </MainInterface>
        )}
        renderBlocked={() => (
            <StateAwareNavigate to="/settings/plans" replace />
        )}
    />
);

export const MainRoutes: React.FC = () => {
    const accountingBreadcrumbs = useAccountingBreadcrumbs();
    const commonRoutes = useRenderCommonRoutes();
    const organizationRedirectRoutes = useRenderOrganizationRedirectRoutes();
    const entityWithMissingProfile = useEntityWithMissingBusinessProfile();
    const canUseConsolidation = useCanUseConsolidation();
    const {
        consolidation: isReportingEnabled,
        schedules: canUseSchedules,
        schedulesExpenses: isExpensesAvailable,
        fixedAssets: canUseFixedAssets,
        form1099NecReport: canUseForm1099NecReport,
        draftsTab,
    } = useTypedFlags();

    const { enabled: isGlFirst, initializing: isGlFirstInitializing } =
        useIsGlFirst();

    const navDisplayConfig = getActionsTasksAccountsNavPaths();
    const scheduleHubScopes = getAvailableScheduleHubScopes({
        isExpensesAvailable,
    });

    return (
        <>
            <Routes>
                {navDisplayConfig.tasksTabPath && (
                    <Route
                        path={navDisplayConfig.tasksTabPath}
                        element={
                            <TasksMainInterface>
                                <TasksPage />
                            </TasksMainInterface>
                        }
                    />
                )}
                <Route
                    path="/activity"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Activity" }]}
                            contained
                        >
                            <HistoryPage />
                        </MainInterface>
                    }
                />
                <Route
                    path="/history"
                    element={<StateAwareNavigate to="/activity" replace />}
                />
                {canUseConsolidation && (
                    <Route
                        path="/ownership"
                        element={
                            <MainInterface
                                breadcrumbs={[{ label: "Ownership" }]}
                                fullPage
                            >
                                <OwnershipPage />
                            </MainInterface>
                        }
                    />
                )}

                <Route
                    path="/invoicing"
                    element={
                        <InvoicingRouteAccess>
                            <MainInterface
                                breadcrumbs={[{ label: "Invoicing" }]}
                            >
                                <InvoicesPage />
                            </MainInterface>
                        </InvoicingRouteAccess>
                    }
                />

                <Route
                    path="/invoicing/items"
                    element={
                        <InvoicingRouteAccess>
                            <MainInterface
                                breadcrumbs={[
                                    { label: "Invoicing", to: "/invoicing" },
                                    { label: "Items" },
                                ]}
                            >
                                <CatalogItemsPage />
                            </MainInterface>
                        </InvoicingRouteAccess>
                    }
                />

                <Route
                    path="/invoicing/templates"
                    element={
                        <InvoicingRouteAccess>
                            <MainInterface
                                breadcrumbs={[
                                    { label: "Invoicing", to: "/invoicing" },
                                    { label: "Templates" },
                                ]}
                            >
                                <InvoiceTemplatesPage />
                            </MainInterface>
                        </InvoicingRouteAccess>
                    }
                />

                {draftsTab && (
                    <Route
                        path="/invoicing/inbox"
                        element={
                            <InvoicingRouteAccess>
                                <MainInterface
                                    breadcrumbs={[
                                        {
                                            label: "Invoicing",
                                            to: "/invoicing",
                                        },
                                        { label: "Inbox" },
                                    ]}
                                >
                                    <AccrualImporterDraftsPage mode="invoices" />
                                </MainInterface>
                            </InvoicingRouteAccess>
                        }
                    />
                )}

                <Route
                    path="/invoicing/preview/:invoiceId"
                    element={
                        <InvoicingRouteAccess>
                            <InvoicePreviewPage />
                        </InvoicingRouteAccess>
                    }
                />

                <Route
                    path="/billing"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Bill pay" }]}>
                            <BillsPage />
                        </MainInterface>
                    }
                />

                {draftsTab && (
                    <Route
                        path="/billing/inbox"
                        element={
                            <MainInterface
                                breadcrumbs={[
                                    { label: "Bill pay", to: "/billing" },
                                    { label: "Inbox" },
                                ]}
                            >
                                <AccrualImporterDraftsPage mode="bills" />
                            </MainInterface>
                        }
                    />
                )}

                <Route
                    path={navDisplayConfig.accountsTabPath}
                    element={
                        <MainInterface breadcrumbs={[{ label: "Accounts" }]}>
                            <AccountsPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/transactions/:entityId?"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Transactions" }]}
                            showSubNavigation
                            contained
                        >
                            <TransactionsPageContainer />
                        </MainInterface>
                    }
                />

                <Route
                    path="/automations"
                    element={<StateAwareNavigate to="/rules" replace />}
                />

                <Route
                    path="/rules"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Rules" }]}>
                            <RulesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/categories"
                    element={
                        isGlFirst && !isGlFirstInitializing ? (
                            <StateAwareNavigate to="/transactions" replace />
                        ) : (
                            <MainInterface
                                breadcrumbs={[
                                    { label: "Transaction categories" },
                                ]}
                            >
                                <CategoriesPage />
                            </MainInterface>
                        )
                    }
                />

                <Route
                    path="/document-categories"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Document categories" }]}
                        >
                            <DocumentCategoriesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/counterparties"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Counterparties" }]}
                        >
                            <DirectorySettings />
                        </MainInterface>
                    }
                />
                <Route
                    path="/directory/*"
                    element={<DirectoryToCounterpartiesRedirect />}
                />

                <Route
                    path="/classes"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Classes" }]}>
                            <ClassesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/schedules"
                    element={<SchedulesToRevenueRedirect />}
                />

                {canUseSchedules &&
                    scheduleHubScopes.map((scope) => (
                        <Route
                            key={scope.path}
                            path={scope.path}
                            element={<ScheduleHubRoute scope={scope} />}
                        />
                    ))}

                {canUseFixedAssets && (
                    <Route
                        path={FIXED_ASSETS_PATH}
                        element={<FixedAssetsRoute />}
                    />
                )}

                <Route
                    path="/insights"
                    element={
                        isGlFirst && !isGlFirstInitializing ? (
                            <StateAwareNavigate to="/" replace />
                        ) : (
                            <MainInterface
                                breadcrumbs={[{ label: "Insights" }]}
                            >
                                <CashFlowPage />
                            </MainInterface>
                        )
                    }
                />

                <Route
                    path="/cashflow"
                    element={<CashflowToInsightsRedirect />}
                />

                <Route
                    path="/documents/:documentId?/:view?"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Documents" }]}>
                            <FinancialDocumentUploadProvider>
                                <FinancialDocumentsPage />
                            </FinancialDocumentUploadProvider>
                        </MainInterface>
                    }
                />

                <Route path="/docs/*" element={<DocsToDocumentsRedirect />} />

                <Route
                    path="/chart-of-accounts"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Chart of Accounts" }]}
                        >
                            <AccountingTabProvider>
                                <ChartOfAccountsPage />
                            </AccountingTabProvider>
                        </MainInterface>
                    }
                />

                <Route
                    path="/reconciliation"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Reconciliation" }]}
                        >
                            <ReconciliationPageWrapper />
                        </MainInterface>
                    }
                />

                <Route
                    path="/reconciliation/report/:bankReconciliationId"
                    element={
                        <MainInterface
                            breadcrumbs={[
                                {
                                    label: "Reconciliation",
                                    to: "/reconciliation",
                                },
                                { label: "Report" },
                            ]}
                        >
                            <BankReconciliationReportPreviewPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/accrual-review"
                    element={
                        <MainInterface
                            breadcrumbs={[
                                {
                                    label: "Accounting",
                                    to: "/accounting",
                                },
                                { label: "Accrual" },
                            ]}
                        >
                            <AccrualReviewPageContainer />
                        </MainInterface>
                    }
                />

                {isReportingEnabled && (
                    <Route
                        path={REPORTING_ROUTE}
                        element={
                            <MainInterface
                                breadcrumbs={[{ label: "Reporting" }]}
                            >
                                <ReportingPage />
                            </MainInterface>
                        }
                    />
                )}

                <Route
                    path="/accounting"
                    element={
                        <MainInterface breadcrumbs={accountingBreadcrumbs}>
                            <AccountingTabProvider>
                                <Outlet />
                            </AccountingTabProvider>
                        </MainInterface>
                    }
                >
                    <Route
                        index
                        element={
                            isReportingEnabled ? (
                                <StateAwareNavigate
                                    to={REPORTING_ROUTE}
                                    replace
                                />
                            ) : (
                                <AccountingPage />
                            )
                        }
                    />
                    <Route
                        path="profit-loss"
                        element={<ProfitLossPreviewPage />}
                    />
                    <Route
                        path="balance-sheet"
                        element={<BalanceSheetPreviewPage />}
                    />
                    <Route
                        path="elimination-detail"
                        element={<EliminationDetailPreviewPage />}
                    />
                    <Route
                        path="elimination-exceptions"
                        element={
                            canUseConsolidation ? (
                                <LegacyIntercompanyTransactionsRedirect />
                            ) : (
                                <EliminationExceptionsPreviewPage />
                            )
                        }
                    />
                    {canUseConsolidation && (
                        <Route
                            path="intercompany-transactions"
                            element={<IntercompanyTransactionsPreviewPage />}
                        />
                    )}
                    <Route
                        path="general-ledger"
                        element={<GeneralLedgerPage />}
                    >
                        <Route
                            path="journal-entries/:journalEntryId"
                            element={<GeneralLedgerJournalEntryRoute />}
                        />
                    </Route>
                    <Route
                        path="chart-of-accounts"
                        element={<ChartOfAccountsPage />}
                    />
                    <Route
                        path="trial-balance"
                        element={<TrialBalancePage />}
                    />
                    {canUseSchedules && (
                        <Route
                            path="schedules"
                            element={<SchedulesReportPageWrapper />}
                        />
                    )}
                    <Route
                        path="expenses-by-vendor"
                        element={<ExpenseByVendorPage />}
                    />
                    <Route
                        path="cash-flow-statement"
                        element={<CashFlowStatementPage />}
                    />
                    <Route
                        path="ar-aging-summary"
                        element={<ArAgingSummaryPageWrapper />}
                    />
                    <Route
                        path="ar-aging-detail"
                        element={<ArAgingDetailPageWrapper />}
                    />
                    <Route path="check-detail" element={<CheckDetailPage />} />
                    {canUseForm1099NecReport && (
                        <Route path="1099-nec" element={<Form1099NecPage />} />
                    )}
                </Route>

                <Route
                    path="/new-entity"
                    element={
                        <FeatureAccess
                            feature={PREMIUM_FEATURES.UNLIMITED_ENTITIES}
                            renderAllowed={() => <NewEntityLegacyPlusGuard />}
                            renderBlocked={() => (
                                <StateAwareNavigate
                                    to="/settings/plans"
                                    replace
                                />
                            )}
                        />
                    }
                />

                <Route
                    path="/create-organization"
                    element={<CreateOrganizationRoute />}
                />

                <Route path="/settings/*" element={<SettingsRoute />} />
                <Route path="/add-account" element={<AddAccountsRoute />} />

                {commonRoutes}
                {organizationRedirectRoutes}

                <Route
                    path="*"
                    element={<StateAwareNavigate to="/" replace />}
                />
            </Routes>

            {entityWithMissingProfile && (
                <EntityProvider entity={entityWithMissingProfile}>
                    <UpdateEntityProfileModal
                        header="Missing information about your business"
                        show
                    />
                </EntityProvider>
            )}
        </>
    );
};
